/**
 * Reader for the PIX "copia e cola" code the `gate2way` invoice returns.
 *
 * The code arrives in the invoice row's `provider_reference` and is a BR Code (EMV) string:
 * it starts with `000201` and carries the `br.gov.bcb.pix` GUI. A Stripe invoice carries its
 * client secret in the same field, so the shape check is what tells the two apart.
 *
 * Pure: no store or network imports. The QA `test_finalize_payment_url` is deliberately NOT
 * read here; it stays on the `qa-finalize.ts` channel.
 */

export interface PixDetails {
  /** The BR Code string to copy and to encode in the QR. */
  code: string;
}

// The code arrives in `provider_reference` (A1, confirmed 2026-10-22). Moving it is a one-line change.
const FIELD_ALIASES = { code: ['provider_reference'] } as const;

/** Plain-object guard that also rejects arrays. */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function isPixCode(value: unknown): value is string {
  return typeof value === 'string' && value.startsWith('000201') && value.includes('br.gov.bcb.pix');
}

export function readPixDetails(invoice: unknown): PixDetails | null {
  if (!isRecord(invoice)) return null;
  const code = invoice[FIELD_ALIASES.code[0]];
  return isPixCode(code) ? { code } : null;
}

export const hasPayablePixDetails = (d: PixDetails | null): d is PixDetails =>
  d != null && d.code.length > 0;
